import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
} from "@mui/material";
import { verifyAndSaveProvider, removeProvider } from "../../utils/api/aiProviderConfig_apis";
import { useSnackbar } from "../../utils/useSnackbar";
import ConfirmationModal from "../Modal/ConfirmationModal";
import ButtonComponent from "../customButton";
import InputBox from "../FormComponent/InputBox";

const KEY_PLACEHOLDER = "••••••••";

export default function ProviderCard({ provider, onUpdate }) {
  const { showSnackbar } = useSnackbar();
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [showConfiguredMask, setShowConfiguredMask] = useState(false);
  const [showSuccessTick, setShowSuccessTick] = useState(false);

  const { key, label, is_own_key, connected } = provider;

  const hasOwnConnection = connected && is_own_key;
  const shouldShowMaskedValue = hasOwnConnection && showConfiguredMask && !apiKeyInput;

  useEffect(() => {
    setShowConfiguredMask(hasOwnConnection);
    setApiKeyInput("");
  }, [hasOwnConnection]);

  useEffect(() => {
    if (!showSuccessTick) return undefined;
    const timer = setTimeout(() => setShowSuccessTick(false), 2200);
    return () => clearTimeout(timer);
  }, [showSuccessTick]);

  const handleVerifySave = async () => {
    if (!apiKeyInput.trim()) {
      showSnackbar("Enter an API key", "error");
      return;
    }
    setSaving(true);
    try {
      const res = await verifyAndSaveProvider(key, apiKeyInput.trim());
      if (res?.data?.success === false) {
        throw new Error(res?.data?.message || "Invalid API key — please check and retry");
      }
      showSnackbar(hasOwnConnection ? "Provider key updated" : "Provider connected", "success");
      setApiKeyInput("");
      setShowConfiguredMask(true);
      setShowSuccessTick(true);
      setTimeout(() => {
        onUpdate?.();
      }, 2300);
    } catch (e) {
      showSnackbar(e.message || "Invalid API key — please check and retry", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      const res = await removeProvider(key);
      if (res?.data?.success === false) {
        throw new Error(res?.data?.message || "Failed to remove provider");
      }
      showSnackbar("Provider removed", "success");
      setConfirmRemove(false);
      onUpdate?.();
    } catch (e) {
      showSnackbar(e.message || "Failed to remove provider", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 1.5,
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {label}
        </Typography>
        {/* SKIP FOR NOW: is_trial → "Trial" badge once trial plan code exists */}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) auto" },
          gap: 1,
          alignItems: "start",
        }}
      >
        <InputBox
          size="small"
          label="API key"
          type="password"
          placeholder={KEY_PLACEHOLDER}
          value={shouldShowMaskedValue ? KEY_PLACEHOLDER : apiKeyInput}
          onChange={(e) => setApiKeyInput(e.target.value)}
          onFocus={() => {
            if (shouldShowMaskedValue) {
              setShowConfiguredMask(false);
            }
          }}
          onBlur={() => {
            if (hasOwnConnection && !apiKeyInput) {
              setShowConfiguredMask(true);
            }
          }}
          autoComplete="new-password"
          className="w-full"
        />

        <Stack direction="row" spacing={1} sx={{ justifySelf: { md: "end" } }}>
          <ButtonComponent
            variant="primary"
            size="small"
            ctaText={showSuccessTick ? "Verified" : hasOwnConnection ? "Verify & Update" : "Verify & Save"}
            endIcon={
              showSuccessTick ? (
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    color: "#FFFFFF",
                    animation: "buttonTickFlow 2200ms ease-in-out",
                    "@keyframes buttonTickFlow": {
                      "0%": { opacity: 0, transform: "translateX(-4px) scale(0.7)" },
                      "20%": { opacity: 1, transform: "translateX(0) scale(1)" },
                      "80%": { opacity: 1, transform: "translateX(0) scale(1)" },
                      "100%": { opacity: 0, transform: "translateX(4px) scale(0.9)" },
                    },
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M13 4.5L6.5 11L3 7.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        strokeDasharray: 18,
                        strokeDashoffset: 18,
                        animation: "drawCheck 2200ms ease-in-out forwards",
                      }}
                    />
                  </svg>
                  <Box
                    sx={{
                      "@keyframes drawCheck": {
                        "0%": { strokeDashoffset: 18 },
                        "25%": { strokeDashoffset: 0 },
                        "100%": { strokeDashoffset: 0 },
                      },
                    }}
                  />
                </Box>
              ) : undefined
            }
            onClick={handleVerifySave}
            disabled={saving}
            loading={saving}
            className="bg-primary white"
          />
          {connected && is_own_key ? (
            <ButtonComponent
              variant="outline"
              size="small"
              ctaText="Remove"
              onClick={() => setConfirmRemove(true)}
              disabled={saving}
              className="primary"
            />
          ) : null}
        </Stack>
      </Box>

      <ConfirmationModal
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={handleRemove}
        title="Remove provider?"
        message="Removing this provider will reset all section assignments that use it"
      />
      </CardContent>
    </Card>
  );
}
